/**
 * Browser Wallet Modal
 *
 * Main modal for wallet connection, creation, and management
 * Shared welcome presentation; authentication gates stay independent.
 */

import { WelcomeLanguage } from "@/components/brand/vnext";
import { useTranslation } from "@/i18n";
import "./wallet-welcome.css";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useBrowserWallet } from "@nullaone/wallet-react";

import { WalletSelectStep } from "./WalletSelectStep";
import { CreateWalletForm } from "./CreateWalletForm";
import { ImportWalletForm } from "./ImportWalletForm";
import { LedgerConnectForm } from "./LedgerConnectForm";
import { MnemonicDisplay } from "./MnemonicDisplay";
import { BackupWarning } from "./BackupWarning";

export function BrowserWalletModal() {
  const { t } = useTranslation();
  const { isModalOpen, closeModal, modalStep, error } = useBrowserWallet();

  const getTitle = () => {
    switch (modalStep) {
      case "select":
        return "Connect Wallet";
      case "create":
        return t("welcome.create");
      case "import":
        return "Import Wallet";
      case "ledger":
        return "Connect Ledger";
      case "mnemonic":
        return "Backup Recovery Phrase";
      case "backup":
        return "Backup Your Wallet";
      default:
        return "Wallet";
    }
  };

  const getDescription = () => {
    switch (modalStep) {
      case "select":
        return "Choose how you want to connect";
      case "create":
        return t("welcome.setupDescription");
      case "import":
        return "Import an existing wallet using your recovery phrase or private key";
      case "ledger":
        return "Connect your Ledger hardware wallet";
      case "mnemonic":
        return "Write down these words in order and keep them safe";
      case "backup":
        return "Download a backup of your encrypted wallet";
      default:
        return "";
    }
  };

  const renderStep = () => {
    switch (modalStep) {
      case "select":
        return <WalletSelectStep />;
      case "create":
        return <CreateWalletForm />;
      case "import":
        return <ImportWalletForm />;
      case "ledger":
        return <LedgerConnectForm />;
      case "mnemonic":
        return <MnemonicDisplay />;
      case "backup":
        return <BackupWarning />;
      default:
        return <WalletSelectStep />;
    }
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="dt-wallet-dialog">
        <div className="dt-wallet-dialog-top"><span>DeTrust</span><WelcomeLanguage /></div>
        <DialogHeader className={modalStep === "create" ? "sr-only" : undefined}>
          <DialogTitle className="text-foreground flex items-center gap-2">
            {getTitle()}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {getDescription()}
          </DialogDescription>
        </DialogHeader>

        {error && modalStep !== "create" && (
          <div className="dt-wallet-error" role="alert">
            {error}
          </div>
        )}

        {renderStep()}

      </DialogContent>
    </Dialog>
  );
}
